import React, { useRef, useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import client from "../../api/client";

/** CLA application form. Posts to /partner-applications; the team is emailed each new one. */

// Must match PartnerApplication::ROLES / ::REACH on the backend.
const ROLES = ["Educator or youth mentor", "Community or faith leader", "Professional", "Student", "Other"];
const REACH = [
  ["1-10", "1–10 people"],
  ["11-50", "11–50 people"],
  ["51-200", "51–200 people"],
  ["200+", "More than 200"],
];
const COUNTRIES = ["Nigeria", "Ghana", "Kenya", "Cameroon", "Sierra Leone", "Liberia", "Zimbabwe", "South Africa", "Uganda", "Tanzania", "Gambia", "United States", "United Kingdom", "Canada"];

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", country: "", city: "", role: "", organisation: "", reach: "", plan: "", heard_from: "", agree: false };

const inputCls = (bad) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-[#0b0f19] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition ${bad ? "border-red-400" : "border-gray-200"}`;

function Field({ label, name, error, optional, children }) {
  return (
    <label className="block" htmlFor={`cla-${name}`}>
      <span className="block text-xs font-semibold text-gray-700 mb-1.5">
        {label} {optional && <span className="font-normal text-gray-400">(optional)</span>}
      </span>
      {children}
      {error && <span className="block mt-1 text-xs text-red-600">{error}</span>}
    </label>
  );
}

export default function ApplyForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const startedAt = useRef(Date.now());

  const set = (name) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setMessage("");
    try {
      await client.post("/partner-applications", { ...form, website: honeypot, form_started_at: startedAt.current });
      setStatus("done");
    } catch (err) {
      const res = err?.response;
      const fieldErrors = res?.data?.errors;
      if (fieldErrors) setErrors(Object.fromEntries(Object.entries(fieldErrors).map(([k, v]) => [k, v[0]])));
      setMessage(
        res?.status === 429
          ? "Too many attempts. Please wait a minute and try again."
          : fieldErrors
            ? "Please check the highlighted fields."
            : res?.data?.message || "Something went wrong. Please try again."
      );
      setStatus("idle");
    }
  };

  if (status === "done") {
    return (
      <div className="rounded-3xl bg-white p-8 md:p-10 text-center shadow-2xl">
        <FiCheckCircle className="mx-auto text-5xl text-primary" />
        <h3 className="mt-5 text-2xl font-bold text-[#0b0f19]">Application received</h3>
        <p className="mt-3 text-gray-600 leading-relaxed">
          Thank you, {form.first_name}. Our partnerships team will review your application and contact you at{" "}
          <strong className="text-[#0b0f19]">{form.email}</strong> with next steps and your referral link.
        </p>
      </div>
    );
  }

  const err = (k) => errors[k];

  return (
    <form onSubmit={submit} noValidate className="rounded-3xl bg-white p-6 md:p-8 shadow-2xl text-left">
      <h3 className="text-xl font-bold text-[#0b0f19]">Apply to become a CLA</h3>
      <p className="mt-1 text-sm text-gray-500">Takes about two minutes. We reply within a few working days.</p>

      {/* Honeypot: hidden from people, filled by bots. */}
      <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] w-px h-px opacity-0" />

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field label="First name" name="first_name" error={err("first_name")}>
          <input id="cla-first_name" className={inputCls(err("first_name"))} value={form.first_name} onChange={set("first_name")} autoComplete="given-name" required />
        </Field>
        <Field label="Last name" name="last_name" error={err("last_name")}>
          <input id="cla-last_name" className={inputCls(err("last_name"))} value={form.last_name} onChange={set("last_name")} autoComplete="family-name" required />
        </Field>
        <Field label="Email" name="email" error={err("email")}>
          <input id="cla-email" type="email" className={inputCls(err("email"))} value={form.email} onChange={set("email")} autoComplete="email" required />
        </Field>
        <Field label="Phone / WhatsApp" name="phone" error={err("phone")}>
          <input id="cla-phone" type="tel" className={inputCls(err("phone"))} value={form.phone} onChange={set("phone")} autoComplete="tel" placeholder="+234 801 234 5678" required />
        </Field>
        <Field label="Country" name="country" error={err("country")}>
          <input id="cla-country" list="cla-countries" className={inputCls(err("country"))} value={form.country} onChange={set("country")} autoComplete="country-name" required />
          <datalist id="cla-countries">{COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
        <Field label="City" name="city" error={err("city")} optional>
          <input id="cla-city" className={inputCls(err("city"))} value={form.city} onChange={set("city")} autoComplete="address-level2" />
        </Field>
        <Field label="Which best describes you?" name="role" error={err("role")}>
          <select id="cla-role" className={inputCls(err("role"))} value={form.role} onChange={set("role")} required>
            <option value="">Choose one</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Organisation or community" name="organisation" error={err("organisation")} optional>
          <input id="cla-organisation" className={inputCls(err("organisation"))} value={form.organisation} onChange={set("organisation")} placeholder="School, church, NGO, company…" />
        </Field>
      </div>

      <fieldset className="mt-5">
        <legend className="text-xs font-semibold text-gray-700 mb-2">How many people could you realistically reach each cohort?</legend>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {REACH.map(([value, label]) => (
            <label key={value} className={`cursor-pointer rounded-xl border px-3 py-2.5 text-center text-xs font-semibold transition ${form.reach === value ? "border-primary bg-orange-50 text-primary" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
              <input type="radio" name="reach" value={value} checked={form.reach === value} onChange={set("reach")} className="sr-only" />
              {label}
            </label>
          ))}
        </div>
        {err("reach") && <span className="block mt-1 text-xs text-red-600">{err("reach")}</span>}
      </fieldset>

      <div className="mt-5 space-y-4">
        <Field label="How would you reach and guide people?" name="plan" error={err("plan")}>
          <textarea id="cla-plan" rows={4} className={inputCls(err("plan"))} value={form.plan} onChange={set("plan")} placeholder="e.g. I run a youth fellowship of 40 people and mentor final-year students at my old school…" required />
        </Field>
        <Field label="How did you hear about the programme?" name="heard_from" error={err("heard_from")} optional>
          <input id="cla-heard_from" className={inputCls(err("heard_from"))} value={form.heard_from} onChange={set("heard_from")} />
        </Field>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={form.agree} onChange={set("agree")} className="mt-1 h-4 w-4 accent-[#d15424]" />
          <span className="text-sm text-gray-600 leading-relaxed">
            I understand this is a performance-based partnership: I earn 5% of the onboarding fee on paid enrolments made through my
            referral link, paid for each completed set of five.
          </span>
        </label>
        {err("agree") && <span className="block -mt-2 text-xs text-red-600">{err("agree")}</span>}
      </div>

      {message && <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{message}</p>}

      <button
        type="submit"
        disabled={status === "sending"}
        className="mt-6 w-full bg-primary hover:bg-[#b0451e] disabled:opacity-60 text-white rounded-full py-3.5 text-xs font-bold uppercase tracking-wider transition-colors"
      >
        {status === "sending" ? "Sending…" : "Submit application"}
      </button>
    </form>
  );
}
